import { getRouteProjects, getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { CaretLeft } from "@phosphor-icons/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { toast } from "sonner";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useProject } from "@/zapi-frontend/pages/UpdateProjectPage/model/useProject";
import { AppButton, LoadingSpinner } from "@libs/zapi-ui/custom";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { useQueryClient } from "@tanstack/react-query";
import {
  ProjectErrors,
  TMinFile,
  TMinFileOrFile,
  TProjectDataWithoutRepository,
  newProjectSchema
} from "@/zapi-frontend/widgets/CreateProjectForm";
import { EditProjectForm } from "@/zapi-frontend/widgets/EditProjectForm";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

export function UpdateProjectPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { isLabs } = useMeStore();

  const { project, loading } = useProject(id || null);
  const navigate = useNavigate();
  const location = useLocation();
  const { documents, createDocument, createWebLink, deleteDocument, refetchDocuments } = useDocuments(
    id || "",
    undefined,
    3000
  );
  const { updateProject } = useProjectStore();

  const [projectData, setProjectData] = useState<TProjectDataWithoutRepository>({
    name: "",
    projectTemplateSlug: "",
    description: "",
    language: "english",
    decisionContext: "",
    successMetrics: "",
    targetAudience: "",
    additionalContext: "",
    chaptersInstructions: {},
    projectInstructions: "",
    presentationPrompt: ""
  });
  const [files, setFiles] = useState<TMinFileOrFile[]>([]);
  const [errors, setErrors] = useState<ProjectErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [deletingFileIds, setDeletingFileIds] = useState<number[]>([]);

  const handleUpdateProject = async () => {
    if (!id) return;
    const validationResult = newProjectSchema.safeParse(projectData);

    if (!validationResult.success) {
      const validationErrors = Object.fromEntries(
        Object.entries(validationResult.error.flatten().fieldErrors).map(([key, errors]) => [key, errors[0]])
      );
      setErrors(validationErrors);

      toast.error("Invalid project data");
      return;
    }
    try {
      setIsLoading(true);

      await updateProject(id, {
        ...validationResult.data
      });
      await queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY], refetchType: "all" });

      toast.success("Project updated successfully");
      navigate(getRouteProjectDetails(id));
    } catch (error) {
      console.error(error);
      toast.error("Failed to update project");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!project) return;
    setProjectData({
      name: project.name,
      description: project.shortDescription || "",
      projectTemplateSlug: project.projectTemplateSlug,
      chaptersInstructions: project.projectChapters.reduce((acc, chapter) => {
        acc[chapter.projectTemplateChapter.slug] = chapter.chapterInstructions ?? "";
        return acc;
      }, {} as Record<string, string>),
      projectInstructions: project.projectInstructions || "",
      decisionContext: project.decisionContext || "",
      successMetrics: project.successMetrics || "",
      targetAudience: project.targetAudience || "",
      additionalContext: project.additionalContext || "",
      language: project.language || "english",
      presentationPrompt: project.presentationPrompt || ""
    });
  }, [project?.id]);

  useEffect(() => {
    const documentsAsFiles: TMinFile[] = documents.map((d) => ({
      name: d.fileName,
      size: d.fileSize,
      id: d.id,
      status: d.status,
      isDeleting: deletingFileIds.includes(d.id),
      isUploading: false,
      webUrl: d.webUrl || undefined
    }));

    const uploadingEntries: TMinFile[] = uploadingFiles.map((fileName, index) => ({
      name: fileName,
      size: 0,
      id: -1 - index,
      status: "pending" as const,
      isDeleting: false,
      isUploading: true
    }));

    setFiles([...documentsAsFiles, ...uploadingEntries]);
  }, [documents, uploadingFiles, deletingFileIds]);

  const isFromProjects = location.state?.from === "projects" || !id;

  if (loading) {
    return (
      <div className="min-h-screen bg-innoveraZinc-100 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-innoveraZinc-100">
      <div className="container mx-auto px-4 py-6">
        <AppButton
          className="mb-6 text-slate-600 text-sm font-normal hover:text-slate-800 transition-colors"
          appearance="text"
          onClick={() => {
            if (isFromProjects) {
              navigate(getRouteProjects());
            } else {
              navigate(getRouteProjectDetails(id));
            }
          }}
        >
          <CaretLeft />
          {isFromProjects ? "Back to projects" : "Back to project"}
        </AppButton>

        <div className={cn("flex flex-col gap-8 max-w-[906px] mx-auto bg-white p-6 rounded-xl")}>
          <EditProjectForm
            projectData={projectData}
            setProjectData={setProjectData}
            errors={errors}
            setErrors={setErrors}
            files={files}
            setFiles={setFiles}
            onFileAdd={async (newFiles) => {
              if (!id) return;

              const fileNames = newFiles.map((f) => f.name);
              setUploadingFiles((prev) => [...prev, ...fileNames]);

              try {
                for (const file of newFiles) {
                  await createDocument(file);
                }
                setUploadingFiles((prev) => prev.filter((name) => !fileNames.includes(name)));
                await refetchDocuments();
              } catch (error) {
                setUploadingFiles((prev) => prev.filter((name) => !fileNames.includes(name)));
                throw error;
              }
            }}
            onFileRemove={async (file) => {
              if (!id || file.id < 0) return;

              setDeletingFileIds((prev) => [...prev, file.id]);

              try {
                await deleteDocument(file.id);
                await refetchDocuments();
              } finally {
                setDeletingFileIds((prev) => prev.filter((fid) => fid !== file.id));
              }
            }}
            onWebLinkAdd={async (url) => {
              if (!id) return;
              await createWebLink(url);
              await refetchDocuments();
            }}
            onSubmit={handleUpdateProject}
            onCancel={() => {
              if (isFromProjects) {
                navigate(getRouteProjects());
              } else {
                navigate(getRouteProjectDetails(id));
              }
            }}
            isLoading={isLoading}
            isLabs={isLabs}
          />
        </div>
      </div>
    </div>
  );
}
